import { Component, Inject } from '@angular/core';

import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

import { MediaService } from '@core/services/media.service';

import {
    DialogContent, DialogResponse,
    SfxDialog
} from '@shared/components/dialog/dialog.component';

import { PlansModel } from '@shared/models/plan.model';
import { BillingOverviewModel } from '@shared/models/billing.model';
import { BehaviorSubject } from 'rxjs';

export interface EditBillingFrequencyContentData {
    selectedValue: string;
    pricingSwitchOptions: BillingOverviewModel;
    editPlan: boolean;
    appliedDiscount?: boolean;
    appliedDiscountType?: string;
}

@Component({
    selector: 'sfx-billing-settings-edit-billing-frequency-content',
    template: `
        <h3 translate>Update billing frequency</h3>
        <sfx-pricing-switch [options]="pricingSwitchOptions" [(selectedValue)]="selectedValue" [editPlan]="editPlan"></sfx-pricing-switch>
    `,
    styles: ['h3 { color: var(--sf-black); font-weight: 700; }']
})
export class EditBillingFrequencyContentComponent implements DialogContent<EditBillingFrequencyContentData> {

    public deleteAutomatedTags = false;

    public valid: true;

    public plans: PlansModel;
    private originalValue: string;
    public selectedValue = '';
    public editPlan = false;
    public pricingSwitchOptions: BillingOverviewModel;

    public appliedDiscount?: boolean;
    public appliedDiscountType?: string;

    public validateState$ = new BehaviorSubject<'valid' | 'invalid' | 'validating'>('invalid');

    constructor(
        public media: MediaService,
        private matDialog: MatDialog,
        private translate: TranslateService,
        @Inject('planService') private plansService: any,
        @Inject('stateService') private stateService: any
    ) {}

    setInitialState(context: EditBillingFrequencyContentData) {

        this.plans = this.plansService.get();

        this.selectedValue = context.selectedValue;
        this.originalValue = context.selectedValue;
        this.editPlan = context.editPlan;
        this.pricingSwitchOptions = context.pricingSwitchOptions;

        this.appliedDiscount = context.appliedDiscount;
        this.appliedDiscountType = context.appliedDiscountType;
    }

    getCurrentState(): EditBillingFrequencyContentData {

        return {
            selectedValue: this.selectedValue,
            editPlan: this.editPlan,
            pricingSwitchOptions: this.pricingSwitchOptions as BillingOverviewModel
        };
    }

    onConfirm(): void {

        this.validateState$.next('validating');

        if (!this.selectedValue || this.selectedValue === this.originalValue) {
            return this.validateState$.next('valid');
        }

        if (this.selectedValue === 'annually') {
            const areYouSure = this.translate.instant('Are you sure?');
            const annualBillingInfo = this.translate.instant('Upon switching to annual billing, you will be charged for the coming year starting today.');
            const paymentCreditInfo = this.translate.instant('Any payments you\'ve already done for future usage will be used as credit towards the charge.');

            const htmlBody = `
              <h3>${areYouSure}</h3>
              <div style="color: var(--sf-black)">
                ${annualBillingInfo}
                <br>
                ${paymentCreditInfo}
              </div>
            `;

            const confirmLabel = this.translate.instant('Yes');
            const closeLabel = this.translate.instant('No');

            this.matDialog.open(SfxDialog, {
                data: {
                    htmlBody,
                    confirm: confirmLabel,
                    close: closeLabel,
                    contentStyle: 'padding: 0 16px;'
                }
            }).afterClosed().subscribe((confirmationResult: DialogResponse) => {
                if (confirmationResult.confirmed) {
                    this.validateState$.next('valid');
                }
                else {
                    this.validateState$.next('invalid');
                }
            });
        }
        else {
            this.validateState$.next('valid');
        }
    }
}
